import Head from 'next/head'

import type { FC, ReactNode } from 'react'
import type { SeoProps } from '@/types'
import type { Locale } from '@/i18n/config'

import { absoluteUrl, siteConfig } from '@/constants/site'
import { defaultLocale, localeMeta, localePath, locales } from '@/i18n/config'
import { useLocale, useT } from '@/i18n'

const toAbsolute = (value?: string): string | undefined => {
    if (!value) return undefined
    if (value.startsWith('http://') || value.startsWith('https://')) return value
    return absoluteUrl(value) || undefined
}

export const Seo: FC<SeoProps> = ({
    title,
    description,
    ogImage,
    imageAlt,
    path = '/',
    type = 'website',
    noIndex = false,
    jsonLd
}): ReactNode => {
    const locale = useLocale()
    const t = useT()
    const metaDescription = description ?? t.meta.siteDescription

    const pageTitle = title
        ? `${title} | ${siteConfig.name}`
        : `${siteConfig.name} | ${t.meta.siteTagline}`

    /** The same page in this language, e.g. /de/libra. Absolute when the site address is configured. */
    const localized = localePath(path, locale)
    const canonical = absoluteUrl(localized) || localized
    const absoluteCanonical = absoluteUrl(localized)
    const image = toAbsolute(ogImage ?? siteConfig.ogImage)
    const isDefaultImage = !ogImage

    const robots = noIndex
        ? 'noindex, nofollow'
        : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'

    const alternates = (locales as Locale[]).map((code) => {
        const href = localePath(path, code)
        return { code, href: absoluteUrl(href) || href }
    })

    return (
        <Head>
            <title>{pageTitle}</title>

            <meta name='description' content={metaDescription} />
            <meta name='robots' content={robots} />
            <meta name='theme-color' content='#161f3d' />
            {!noIndex && <link rel='canonical' href={canonical} />}
            {!noIndex &&
                alternates.map((alternate) => (
                    <link key={alternate.code} rel='alternate' hrefLang={alternate.code} href={alternate.href} />
                ))}
            {!noIndex && (
                <link
                    rel='alternate'
                    hrefLang='x-default'
                    href={alternates.find((alternate) => alternate.code === defaultLocale)?.href ?? canonical}
                />
            )}

            <meta property='og:title' content={pageTitle} />
            <meta property='og:description' content={metaDescription} />
            <meta property='og:type' content={type} />
            <meta property='og:site_name' content={siteConfig.name} />
            <meta property='og:locale' content={localeMeta[locale].ogLocale} />
            {(locales as Locale[])
                .filter((code) => code !== locale)
                .map((code) => (
                    <meta key={code} property='og:locale:alternate' content={localeMeta[code].ogLocale} />
                ))}
            {absoluteCanonical && <meta property='og:url' content={absoluteCanonical} />}
            {image && <meta property='og:image' content={image} />}
            {image && isDefaultImage && <meta property='og:image:width' content='1200' />}
            {image && isDefaultImage && <meta property='og:image:height' content='630' />}
            {image && <meta property='og:image:alt' content={imageAlt ?? pageTitle} />}

            <meta name='twitter:card' content='summary_large_image' />
            <meta name='twitter:title' content={pageTitle} />
            <meta name='twitter:description' content={metaDescription} />
            {image && <meta name='twitter:image' content={image} />}
            {image && <meta name='twitter:image:alt' content={imageAlt ?? pageTitle} />}

            {jsonLd && (
                <script
                    type='application/ld+json'
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
                />
            )}
        </Head>
    )
}
